"use client";

import { fetchApplicationList } from "@/api/user/fetchApplicationList";
import { getSavedApplicationList } from "@/api/user/getSavedApplicationList";
import { ContainerXL } from "@/common";
import DataTable from "@/common/data-table.common";
import NavigationLink from "@/common/navigation-link.common";
import { ApplicationStatus } from "@/constants";
import { formatDate } from "@/utils/utils";
import { Refresh } from "@mui/icons-material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  Alert,
  Button,
  IconButton,
  Card as MuiCard,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import * as React from "react";
import { Badge } from "../ui/badges";
import Card from "./card.component";

const getStatusColor = (status) => {
  switch (status) {
    case "In Progress":
      return "warning";
    case "Rejected":
      return "error";
    case "Approved":
      return "success";
    default:
      return "default";
  }
};

const headCells = [
  {
    id: "tracking_id",
    label: "Tracking ID",
    Cell: ({ row }) => {
      const isDraft = row?.isDraft;
      const href = isDraft
        ? `/new-application?trackingId=${row.tracking_id}`
        : `/application-detail?trackingId=${row.tracking_id}`;

      return (
        <NavigationLink href={href}>
          <Button variant="text">{row.tracking_id}</Button>
        </NavigationLink>
      );
    },
  },
  {
    id: "submission_date",
    label: "Submission Date",
    Cell: ({ row }) => formatDate(row?.submission_date),
    hideMobile: true,
  },
  {
    id: "purpose",
    label: "Purpose",
  },
  {
    id: "status",
    label: "Status",
    Cell: ({ row }) => {
      const t = useTranslations();
      
      return (
        <Badge variant={row?.status?.toLowerCase()} className="capitalize">
          {t(row?.status)}
        </Badge>
      );
    },
  },
  {
    id: "last_updated",
    label: "Last Updated",
    Cell: ({ row }) => formatDate(row?.last_updated),
    hideMobile: true,
  },
];

const RenderDataTableContent = ({ applicationList, error, loading, children }) => {
  const t = useTranslations();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (loading) {
    return (
      <div className="p-2">
        <Alert severity="info" variant="standard">
          {t("Loading applications")}
        </Alert>
        <div className="mt-4">
          <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "4px", marginBottom: "0.5rem" }} />
          {[...Array(3)].map((_, rowIndex) => (
            <div key={rowIndex} className="flex gap-4 mb-2">
              {[...Array(isMobile ? 3 : 5)].map((_, colIndex) => (
                <Skeleton key={colIndex} variant="rounded" width={isMobile ? "33%" : "20%"} height={40} sx={{ borderRadius: "4px" }} />
              ))}
            </div>
          ))}
          <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "4px" }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert severity="error" variant="standard">
          {t("Error loading applications")}: {error?.message ?? error}
        </Alert>
      </div>
    );
  }

  if (applicationList.length === 0) {
    return (
      <div className="flex flex-col gap-4 items-center justify-center py-12">
        <Typography variant="h6">{t("No Applications Found")}</Typography>
        <Typography color="light-grey" variant="body1">
          {t("You have not submitted any applications yet")}
        </Typography>
      </div>
    );
  }

  return children;
};

export const LandingPage = () => {
  const t = useTranslations();
  const [filter, setFilter] = React.useState(ApplicationStatus.All);
  const [applicationList, setApplicationList] = React.useState([]);
  const [applicationCounts, setApplicationCounts] = React.useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchSavedApplicationListMutation = useMutation({
    mutationFn: getSavedApplicationList,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 3000),
    onSuccess: (savedData) => {
      const regularApplications =
        fetchApplicationListMutation.data?.application_list?.map((item) => ({
          ...item,
          id: item.tracking_id,
        })) || [];

      const savedApplications =
        savedData?.map((item) => ({
          tracking_id: item.tracking_id.toString(),
          submission_date: item.last_modified,
          purpose: "Draft Application",
          status: t("Draft"),
          last_updated: item.last_modified,
          id: `draft-${item.tracking_id}`,
          isDraft: true,
        })) || [];

      const combinedApplications = [...regularApplications, ...savedApplications];

      const updatedCounts = {
        ...applicationCounts,
        drafts: savedApplications.length,
        total_applications:
          (applicationCounts?.total_applications || 0) + savedApplications.length,
      };

      setApplicationList(combinedApplications);
      setApplicationCounts(updatedCounts);
    },
    onError: (error) => {
      console.error("Error fetching saved applications:", error);
    },
  });

  const fetchApplicationListMutation = useMutation({
    mutationFn: fetchApplicationList,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 3000),
    onSuccess: (data) => {
      const regularApplications =
        data?.application_list?.map((item) => ({
          ...item,
          id: item.tracking_id,
        })) || [];

      setApplicationList(regularApplications);
      setApplicationCounts(data?.application_counter || {});

      fetchSavedApplicationListMutation.mutate({
        request: localStorage.getItem("token"),
      });
    },
    onError: (error) => {
      console.warn("Retrying fetchApplicationList failed:", error);
    },
  });

  const isLoading =
    fetchApplicationListMutation.isLoading || fetchSavedApplicationListMutation.isLoading;

  const isError =
    !isLoading &&
    (fetchApplicationListMutation.isError || fetchSavedApplicationListMutation.isError);

  const error =
    isError &&
    (fetchApplicationListMutation.error || fetchSavedApplicationListMutation.error);

  React.useEffect(() => {
    fetchApplicationListMutation.mutate({
      request: localStorage.getItem("token"),
    });
  }, []);

  const refreshApplications = () => {
    fetchApplicationListMutation.mutate({
      request: localStorage.getItem("token"),
    });
  };

  // Get filtered columns based on screen size
  const getResponsiveColumns = () => {
    if (isMobile) {
      return headCells.filter(column => !column.hideMobile);
    }
    return headCells;
  };

  return (
    <div className="h-full flex flex-col gap-8 bg-gray-100 pb-12">
      <div className="bg-green-100 py-6">
        <ContainerXL>
          <Typography variant={isMobile ? "h5" : "h3"} sx={{ 
            fontSize: isMobile ? "1.25rem" : undefined,
            lineHeight: isMobile ? "1.6" : undefined
          }}>
            {t("Portal to FastTrack Regulated activities in ESZ of Nagpur district")}
          </Typography>
        </ContainerXL>
      </div>

      <ContainerXL>
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-wrap'} gap-6`}>
          <Card heading="Total Applications" value={applicationCounts?.total_applications ?? 0} icon={<DescriptionOutlinedIcon color="info" />} />
          <Card heading="Pending" value={applicationCounts?.in_review ?? 0} icon={<AccessTimeRoundedIcon color="warning" />} />
          <Card heading="Approved" value={applicationCounts?.approved ?? 0} icon={<CheckCircleOutlineRoundedIcon color="success" />} />
          <Card heading="Rejected" value={applicationCounts?.rejected ?? 0} icon={<CancelOutlinedIcon color="error" />} />
          {applicationCounts?.drafts > 0 && (
            <Card heading="Drafts" value={applicationCounts?.drafts ?? 0} icon={<DescriptionOutlinedIcon color="default" />} />
          )}
        </div>
      </ContainerXL>

      <ContainerXL>
        <MuiCard sx={{ padding: "0", gap: "0" }} elevation={1}>
          <div className={`${isMobile ? 'flex flex-col gap-4 items-center p-4' : 'flex justify-between items-center p-6'}`}>
  <Typography variant="h5" className={`${isMobile ? 'text-center' : ''}`}>
    {t("My Applications")}
  </Typography>
  <div className={`${isMobile ? 'flex flex-col gap-2 w-full' : 'flex gap-2'}`}>
    <Tooltip title="Refresh Applications">
      <IconButton 
        onClick={refreshApplications} 
        className={`${isMobile ? 'self-center' : ''}`}
      >
        <Refresh color="success" />
      </IconButton>
    </Tooltip>
    <NavigationLink href="/new-application">
      <Button 
        variant="contained" 
        startIcon={<AddOutlinedIcon />}
        fullWidth={isMobile}
        sx={{ width: isMobile ? '100%' : 'auto' }}
      >
        {t("New Application")}
      </Button>
    </NavigationLink>
  </div>
</div>
          <RenderDataTableContent
            applicationList={applicationList}
            error={error}
            loading={isLoading}
          >
            <DataTable
              data={applicationList}
              columns={getResponsiveColumns()}
              dataFilterFn={({ status }) =>
                filter === ApplicationStatus.All || status === filter
              }
              initialOrderBy="submissionDate"
              filter={filter}
              setFilter={setFilter}
              filters={ApplicationStatus}
              responsive={true}
              sx={{
                '.MuiTableCell-root': {
                  padding: isMobile ? '8px' : undefined,
                  fontSize: isMobile ? '0.875rem' : undefined,
                },
                '.MuiButton-root': {
                  padding: isMobile ? '4px 8px' : undefined,
                  fontSize: isMobile ? '0.75rem' : undefined,
                }
              }}
            />
          </RenderDataTableContent>
        </MuiCard>
      </ContainerXL>
    </div>
  );
};

export default LandingPage;